<template>
  <Dialog v-if="visible" :title="'View as…'" :width="560" class="imp-picker" @close="close">
    <p class="ip-intro">
      See this loan in swiftLoan as someone you manage sees it. Starting and stopping is logged under your name, the
      same as in swiftMarketing and swiftApp.
    </p>

    <div class="ip-search">
      <i class="fa-light fa-magnifying-glass"></i>
      <input
        ref="searchInput"
        v-model="search"
        type="text"
        placeholder="Search by name, email, title or branch"
        aria-label="Search people"
      />
    </div>

    <div v-if="groups.length > 1" class="ip-tabs" role="tablist">
      <button
        v-for="g in groups"
        :key="g.level"
        type="button"
        role="tab"
        :aria-selected="g.level === level"
        :class="['ip-tab', { 'is-on': g.level === level }]"
        @click="pickLevel(g.level)"
      >
        {{ g.label }} <span>{{ g.count }}</span>
      </button>
    </div>

    <div v-if="error" class="ip-msg" role="alert">{{ error }}</div>

    <ul class="ip-list" aria-label="People you can view as">
      <li v-for="t in items" :key="t.target">
        <button type="button" class="ip-row" :disabled="!!starting" @click="choose(t)">
          <span class="ip-avatar">{{ initials(t.name) }}</span>
          <span class="ip-who">
            <strong>{{ t.name }}</strong>
            <small>
              {{ t.role_label }}<template v-if="t.title && t.title !== t.role_label"> · {{ t.title }}</template>
              <template v-if="t.orgid"> · Branch {{ t.orgid }}</template>
              <template v-if="t.division"> · {{ t.division }}</template>
            </small>
          </span>
          <i v-if="starting === t.target" class="fa-light fa-spinner-third fa-spin"></i>
          <i v-else class="fa-light fa-eye ip-go"></i>
        </button>
      </li>
    </ul>

    <p v-if="!loading && !items.length" class="ip-empty">{{ search ? 'Nobody matches that search.' : 'There is nobody you can view as.' }}</p>
    <p v-if="loading" class="ip-empty"><i class="fa-light fa-spinner-third fa-spin"></i> Loading…</p>

    <DialogActionsBar>
      <div class="ip-footer">
        <span class="ip-count">{{ items.length }} of {{ total }}</span>
        <button v-if="items.length < total" type="button" class="ip-btn" :disabled="loading" @click="more">Show more</button>
        <button type="button" class="ip-btn" :disabled="!!starting" @click="close">Close</button>
      </div>
    </DialogActionsBar>
  </Dialog>
</template>

<script>
// "View as…" picker, ported from swiftMarketing (connectGS3.0 components/shell/ImpersonationDialog.vue); the same
// component swiftApp has. Lists only the people the server says the signed-in (real) user may view as, one group per
// tab (People you work for / Everyone / Division / Branch). Realtor partners are left out: swiftLoan has no realtor
// experience. Picking someone calls Start (server validates + audits) and reloads this page as them.
import { Dialog, DialogActionsBar } from "@progress/kendo-vue-dialogs"
import impersonationApi from "@/api/impersonation.service.js"
import { startImpersonation } from "@/helpers/impersonation.js"

const PAGE = 25
const isStaff = x => x && x.level !== "realtors" && x.kind !== "realtor"

export default {
  name: "ImpersonationDialog",
  components: { Dialog, DialogActionsBar },
  props: {
    visible: { type: Boolean, default: false }
  },
  emits: ["update:visible"],
  data() {
    return {
      search: "",
      level: null,
      groups: [],
      items: [],
      total: 0,
      page: 1,
      loading: false,
      starting: null,
      error: null,
      timer: null,
      seq: 0
    }
  },
  watch: {
    search() {
      clearTimeout(this.timer)
      this.timer = setTimeout(this.reset, 250)
    },
    visible: {
      immediate: true,
      handler(v) {
        if (!v) return
        this.search = ""
        this.level = null
        this.items = []
        this.groups = []
        this.error = null
        this.starting = null
        this.reset()
        this.$nextTick(() => this.$refs.searchInput?.focus())
      }
    }
  },
  beforeUnmount() {
    clearTimeout(this.timer)
  },
  methods: {
    async load({ append = false } = {}) {
      const mine = ++this.seq
      this.loading = true
      this.error = null
      try {
        const res = await impersonationApi.targets({ search: this.search.trim(), level: this.level, page: this.page, pageSize: PAGE })
        if (mine !== this.seq) return
        this.groups = (res?.groups || []).filter(isStaff)
        const rows = (res?.items || []).filter(isStaff)
        this.level = rows[0]?.level || this.groups.find(g => g.level === this.level)?.level || this.groups[0]?.level || null
        this.items = append ? [...this.items, ...rows] : rows
        // When the server answered with the realtors group (no staff matched), there is nothing to show here.
        const staffPage = rows.length > 0 || append
        this.total = staffPage ? res?.total || 0 : 0
      } catch (err) {
        if (mine === this.seq) this.error = err.message
      } finally {
        if (mine === this.seq) this.loading = false
      }
    },
    reset() {
      this.page = 1
      this.load()
    },
    pickLevel(l) {
      if (l === this.level) return
      this.level = l
      this.reset()
    },
    more() {
      this.page += 1
      this.load({ append: true })
    },
    async choose(t) {
      this.starting = t.target
      this.error = null
      try {
        await startImpersonation(t.target) // reloads the app on success
      } catch (err) {
        this.error = err.message
        this.starting = null
      }
    },
    close() {
      if (this.starting) return
      this.$emit("update:visible", false)
    },
    initials(name) {
      return String(name || "?")
        .split(/\s+/)
        .map(p => p[0])
        .slice(0, 2)
        .join("")
        .toUpperCase()
    }
  }
}
</script>

<style scoped>
.ip-intro {
  margin: 0 0 12px;
  font-size: 13px;
  line-height: 1.5;
  color: #64748b;
}

.ip-search {
  position: relative;
}

.ip-search i {
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: #64748b;
}

.ip-search input {
  width: 100%;
  box-sizing: border-box;
  padding: 9px 12px 9px 34px;
  border: 1px solid #d6dce4;
  border-radius: 8px;
  font-size: 14px;
  color: var(--primary, #051d3a);
  outline: none;
}

.ip-search input:focus {
  border-color: #2563eb;
  box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.15);
}

.ip-tabs {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 12px;
}

.ip-tab {
  padding: 4px 12px;
  border: 1px solid #d6dce4;
  border-radius: 999px;
  background: #fff;
  color: var(--primary, #051d3a);
  font-size: 12.5px;
  cursor: pointer;
}

.ip-tab span {
  color: #64748b;
  margin-left: 4px;
}

.ip-tab.is-on {
  border-color: #2563eb;
  background: #2563eb;
  color: #fff;
}

.ip-tab.is-on span {
  color: rgba(255, 255, 255, 0.8);
}

.ip-msg {
  margin-top: 12px;
  padding: 8px 12px;
  border-radius: 8px;
  background: #fef2f2;
  border: 1px solid #fecaca;
  color: #b91c1c;
  font-size: 13px;
}

.ip-list {
  list-style: none;
  margin: 12px 0 0;
  padding: 0;
  max-height: 46vh;
  overflow: auto;
}

.ip-row {
  width: 100%;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 10px;
  border: 0;
  border-radius: 10px;
  background: transparent;
  color: var(--primary, #051d3a);
  text-align: left;
  cursor: pointer;
}

.ip-row:hover:not(:disabled) {
  background: #fff7e6;
}

.ip-row:disabled {
  cursor: default;
}

.ip-avatar {
  width: 34px;
  height: 34px;
  flex: 0 0 34px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: #051d3a;
  color: #f2b705;
  font-weight: 700;
  font-size: 12px;
}

.ip-who {
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  flex-direction: column;
  line-height: 1.25;
}

.ip-who strong {
  font-size: 13.5px;
}

.ip-who small {
  font-size: 12px;
  color: #64748b;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.ip-go {
  color: #64748b;
}

.ip-empty {
  margin: 14px 0 4px;
  font-size: 13px;
  text-align: center;
  color: #64748b;
}

.ip-footer {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 4px 8px;
}

.ip-count {
  margin-right: auto;
  font-size: 12px;
  color: #64748b;
}

.ip-btn {
  padding: 6px 14px;
  border: 1px solid #d6dce4;
  border-radius: 8px;
  background: #fff;
  color: var(--primary, #051d3a);
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
}

.ip-btn:hover:not(:disabled) {
  background: #f4f6f9;
}

.ip-btn:disabled {
  opacity: 0.6;
  cursor: default;
}
</style>
